import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardActions from '@material-ui/core/CardActions'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Box from '@material-ui/core/Box'
import Alert from '@material-ui/lab/Alert'
import { red } from '@material-ui/core/colors'
import AttachmentIcon from '@material-ui/icons/Attachment'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import RoomIcon from '@material-ui/icons/Room'
import { format, parse } from 'date-fns'

import Grid from '@material-ui/core/Grid'
import DescriptionIcon from '@material-ui/icons/Description'
import { CircularProgress } from '@material-ui/core'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'

import { KeyboardDatePicker } from '@material-ui/pickers'
import { useAddNoteMutation } from '@graniot/common/adapters/note'

import { LayerSelector } from '../LayerSelector'

import NoteMap from './NoteMap'
import GeomNote from './GeomNote'
import { useParcelPlants } from '../../../../adapters/plants'

const useStyles = makeStyles((theme) => ({
	root: {
		width: '100%',
	},
	card: {
		marginBottom: theme.spacing(1),
	},
	save: {
		marginLeft: 'auto',
	},
	date: {
		marginTop: theme.spacing(2),
	},
	avatar: {
		backgroundColor: red[500],
	},
	attachment: {
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(0.5),
	},
	mapNoteContainer: {
		minHeight: 400,
	},
}))

function reducer(state, action) {
	switch (action.type) {
		case 'add':
			return state.concat([action.marker])
		case 'remove':
			return state.filter(
				(p) => JSON.stringify(p) !== JSON.stringify(action.marker)
			)
		case 'reset':
			return new Array()
		default:
			throw new Error()
	}
}

export default function FormNote({
	parcel,
	layers,
	selectedResolution,
	refetchList,
	maxCC,
	setMaxCC,
	from_date,
	toDate,
	isLoadingParcelFISHistory,
	dataParcelFISHistory,
	errorParcelFISHistory,
	refetchParcelFISHistory,
	selectedLayer,
	handleSelectedLayer,
}) {
	const classes = useStyles()
	const [attachments, setAttachments] = React.useState([])
	const [note, setNote] = React.useState('')
	const [noteProperties, setNoteProperties] = React.useState({
		name: '',
		description: '',
	})
	const [referenceDate, setReferenceDate] = React.useState(
		format(new Date(), 'dd/MM/yyyy')
	)
	const [addPosition, setAddPosition] = React.useState(false)
	const [noteMarker, dispatch] = React.useReducer(reducer, [])

	const [selectedLayerDate, setSelectedLayerDate] = React.useState(null)

	const [noteError, setNoteError] = React.useState(false)

	const [featureStats, setFeatureStats] = React.useState(null)
	const [showTrees, setShowTrees] = React.useState(false)

	const { t } = useTranslation()

	const mutateNote = useAddNoteMutation()
	
	// Fetch tree data to check if trees exist - only when add position is active
	const { data: treeData, isLoading: isLoadingTreeData, error: treeDataError } = useParcelPlants(parcel?.id, {
		enabled: addPosition
	})
	
	// Check for parcel size error
	const parcelSizeError = errorParcelFISHistory?.error_code === 'PARCEL_SIZE_EXCEEDS_LIMIT' ? errorParcelFISHistory : null
	const statsLimitError = errorParcelFISHistory?.error_code === 'PARCEL_EXCEEDS_STATS_LIMIT' ? errorParcelFISHistory : null

	React.useEffect(() => {
		if (from_date) {
			refetchParcelFISHistory()
		}
	}, [selectedLayer, maxCC, parcel])

	React.useEffect(() => {
		if (dataParcelFISHistory) {
			let feature_stats = Object.assign(
				[],
				dataParcelFISHistory
			)
			feature_stats.reverse()
			setFeatureStats(feature_stats)
		}
	}, [dataParcelFISHistory])

	React.useEffect(() => {
		if (featureStats && featureStats.length >= 1) {
			setSelectedLayerDate(featureStats[featureStats.length - 1].date)
		}
	}, [featureStats])

	React.useEffect(() => {
		if (!mutateNote.isLoading && mutateNote.isSuccess) {
			mutateNote.reset()
			setAttachments([])
			setNote('')
			setAddPosition(false)
			refetchList()
		}
		if (mutateNote.isError) console.log(mutateNote)
	}, [mutateNote])

	React.useEffect(() => {
		if (!addPosition) {
			dispatch({ type: 'reset' })
		}
	}, [addPosition])

	const onChange = (e) => {
		setAttachments([...e.target.files, ...attachments])
	}

	const handleDelete = (index) => {
		attachments.splice(index, 1)
		setAttachments([...attachments])
	}

	const handleChange = (e) => {
		setNote(e.target.value)
	}

	const handleChangeProperties = (e, geom) => {
		setNoteProperties((previousState) => ({
			...previousState,
			[e.target.name]: e.target.value,
		}))
		geom.properties = {
			...geom.properties,
			[e.target.name]: e.target.value,
		}
	}

	const handleChangeReferenceDate = (date) => {
		setReferenceDate(date)
	}

	const saveNote = () => {
		let obj = {
			note: note,
			attachments: attachments,
			parcel: parcel.id,
			reference_date: referenceDate,
		}
		if (noteMarker.length > 0) {
			obj['markers'] = noteMarker
			// Only include layer information if there's no parcel size error
			if (!parcelSizeError && !statsLimitError) {
				obj['layer_name'] = selectedLayer.name
				obj['layer_date'] = selectedLayerDate
			}
		}
		mutateNote.mutate(obj)
	}

	const handleAddPosition = () => {
		setAddPosition(!addPosition)
	}

	const handleShowTreesToggle = () => {
		console.log('Toggle trees from:', showTrees, 'to:', !showTrees);
		setShowTrees(!showTrees)
	}

	function indexOfDate(date) {
		return featureStats
			.map((d) => format(new Date(d.date), 'dd/MM/yyyy'))
			.indexOf(date)
	}

	function disableNotAvalibleSentinelImages(date) {
		return indexOfDate(date) === -1
	}

	const handleChangeComparedDate = (comparedDate) => {
		setSelectedLayerDate(featureStats[indexOfDate2(comparedDate)].date)
	}

	function indexOfDate2(date) {
		return featureStats.findIndex((d) => {
			return new Date(d.date) - parse(date, 'dd/MM/yyyy', new Date()) > 0
		})
	}

	return (
		<Card className={classes.root} variant="outlined">
			<CardContent>
				<Grid container spacing={3}>
					{/* When addPosition is false, show Note and Reference Date in same row */}
					{!addPosition && (
						<>
							<Grid item xs={12} sm={8} md={8}>
								<TextField
									id="filled-multiline-flexible"
									label={t(`Note.Add Note`)}
									multiline
									maxRows={4}
									fullWidth
									value={note}
									onChange={handleChange}
									error={mutateNote.isError}
									helperText={mutateNote?.error?.note?.join(' ')}
								/>
							</Grid>
							<Grid item xs={12} sm={4} md={4}>
								<KeyboardDatePicker
									label={t(`Note.Reference date`)}
									name="reference_date"
									value={parse(
										referenceDate,
										'dd/MM/yyyy',
										new Date()
									)}
									onChange={(date) =>
										handleChangeReferenceDate(
											format(date, 'dd/MM/yyyy')
										)
									}
									format="dd/MM/yyyy"
									variant="inline"
									fullWidth
									autoOk
								/>
							</Grid>
						</>
					)}

					{/* When addPosition is true, show Note full width, then Reference Date in control row */}
					{addPosition && (
						<Grid item xs={12}>
							<TextField
								id="filled-multiline-flexible"
								label={t(`Note.Add Note`)}
								multiline
								maxRows={4}
								fullWidth
								value={note}
								onChange={handleChange}
								error={mutateNote.isError}
								helperText={mutateNote?.error?.note?.join(' ')}
							/>
						</Grid>
					)}

					{addPosition && (
						<>
							{parcelSizeError && (
								<Grid item xs={12}>
									<Alert severity="info">
										{t('satellite_layer_unavailable_size_limit', {
											hectares: parcelSizeError.details?.parcel_hectares || 'N/A',
											maxLimit: parcelSizeError.details?.max_limit || 'N/A'
										})}
									</Alert>
								</Grid>
							)}
							{statsLimitError && (
								<Grid item xs={12}>
									<Alert severity="warning">{t('parcel_exceeds_stats_limit')}</Alert>
								</Grid>
							)}

							{/* Row 2: Control Row - Reference Date, Layer Selector, Layer Date */}
							<Grid item xs={12} sm={6} md={4}>
								<KeyboardDatePicker
									label={t(`Note.Reference date`)}
									name="reference_date"
									value={parse(
										referenceDate,
										'dd/MM/yyyy',
										new Date()
									)}
									onChange={(date) =>
										handleChangeReferenceDate(
											format(date, 'dd/MM/yyyy')
										)
									}
									format="dd/MM/yyyy"
									variant="inline"
									fullWidth
									autoOk
								/>
							</Grid>

							{!parcelSizeError && !statsLimitError && (
								<Grid item xs={12} sm={6} md={4}>
									<LayerSelector
										layers={layers}
										selectedLayer={selectedLayer}
										handleSelectedLayer={handleSelectedLayer}
										width={'100%'}
									/>
								</Grid>
							)}

							{!parcelSizeError && !statsLimitError && (
								<Grid item xs={12} sm={12} md={4}>
									{isLoadingParcelFISHistory || !selectedLayerDate ? (
										<Box display="flex" alignItems="center" justifyContent="center" height={56}>
											<CircularProgress size={24} />
										</Box>
									) : (
										<KeyboardDatePicker
											label={t(`Note.Layer date`)}
											name="selected_layer_date"
											value={selectedLayerDate}
											onChange={(date) =>
												handleChangeComparedDate(
													format(date, 'dd/MM/yyyy')
												)
											}
											shouldDisableDate={(date) =>
												disableNotAvalibleSentinelImages(
													format(date, 'dd/MM/yyyy')
												)
											}
											maxDate={new Date()}
											minDate={new Date(from_date)}
											format="dd/MM/yyyy"
											variant="inline"
											fullWidth
											autoOk
										/>
									)}
								</Grid>
							)}

							{/* Row 3: Map (left) and Tree Switch + GeomNote List (right) */}
							<Grid item xs={12} sm={8} md={9} className={classes.mapNoteContainer}>
								<NoteMap
									selectedResolution={selectedResolution}
									layer={selectedLayer}
									parcel={parcel}
									noteMarker={noteMarker}
									dispatch={dispatch}
									selectedLayerDate={selectedLayerDate}
									showTrees={showTrees}
								/>
								<Typography variant="subtitle2" gutterBottom>
									{t('Note.Click inside parcel to save a position with the note.')}
								</Typography>
							</Grid>

							<Grid item xs={12} sm={4} md={3}>
								{(isLoadingTreeData || (treeData && treeData.features && treeData.features.length > 0)) && (
									<>
										<FormControlLabel
											control={
												<Switch
													checked={showTrees}
													onChange={handleShowTreesToggle}
													color="primary"
													disabled={isLoadingTreeData}
												/>
											}
											label={
												isLoadingTreeData
													? `${t('Show Trees')} (Loading...)`
													: treeData?.features?.length
														? `${t('Show Trees')} (${treeData.features.length})`
														: `${t('Show Trees')} (0)`
											}
										/>
										{treeDataError && (
											<Typography variant="caption" color="error" display="block">
												Error loading trees: {treeDataError.message}
											</Typography>
										)}
									</>
								)}

								{/* GeomNote List - moved to right column */}
								{noteMarker.length > 0 && (
									<div style={{ marginTop: 16, height: 350, overflow: 'auto' }}>
										{noteMarker.map((geometry) => (
											<GeomNote
												key={geometry.id}
												geometry={geometry}
												disabled={false}
												handleChangeProperties={handleChangeProperties}
											/>
										))}
									</div>
								)}
							</Grid>
						</>
					)}
					<Grid item xs={12} sm={6} md={4}>
						{attachments.map((att, index) => (
							<Chip
								key={index}
								label={att.name}
								variant="outlined"
								onDelete={() => handleDelete(index)}
								className={classes.attachment}
								icon={<DescriptionIcon />}
							/>
						))}
					</Grid>
				</Grid>
			</CardContent>
			<CardActions disableSpacing>
				<input
					accept="*"
					id="contained-button-file"
					multiple
					type="file"
					hidden
					onChange={onChange}
				/>
				<label htmlFor="contained-button-file">
					<Button
						component="span"
						startIcon={<AttachmentIcon />}
						disableElevation
					>
						{t('Note.Upload Files')}
					</Button>
				</label>

				<Button
					component="span"
					onClick={handleAddPosition}
					startIcon={<RoomIcon />}
					disableElevation
					style={{ marginLeft: 8 }}
				>
					{addPosition
						? t('Note.Close map')
						: t('Note.Add position')}
				</Button>

				<Button
					color="primary"
					variant="contained"
					onClick={saveNote}
					startIcon={
						mutateNote.isLoading && (
							<CircularProgress
								color="inherit"
								size={20}
							/>
						)
					}
					className={classes.save}
				>
					{t('Note.Save')}
				</Button>
			</CardActions>
		</Card>
	)
}
